/**
 * @quotech/ui - Universal Integrations Settings Tab
 *
 * PATH: packages/ui/src/components/settings/IntegrationsTab.tsx
 * PURPOSE: Display and manage third-party integrations (Outlook, OneDrive, Xero, Simpro)
 * WHY: Admin-only tab. Hub-agnostic — receives integration status via props.
 *
 * FLOW:
 * ┌──────────────────┐    ┌─────────────────┐    ┌─────────────────────────────┐
 * │ IntegrationStatus│ → │ IntegrationsTab  │ → │ Grouped list of cards       │
 * │ [] (props)       │    │ (container)      │    │ (connected/available/soon)  │
 * └──────────────────┘    └─────────────────┘    └─────────────────────────────┘
 *
 * DEPENDENCIES:
 * - ./integrations-tab/integrations-tab-types: shared type definitions
 * - ./integrations-tab/IntegrationCard: individual integration card component
 * - lucide-react: Loader2, Zap icons for loading/empty states
 * - ../../lib/utils (cn): conditional classnames
 */

import { useState, useCallback } from 'react'
import { Loader2, Zap } from 'lucide-react'
import { cn } from '../../lib/utils'
import { IntegrationCard } from './integrations-tab/IntegrationCard'

// Re-export types so existing consumers (`import { IntegrationStatus } from './IntegrationsTab'`) keep working
export type { IntegrationStatus, IntegrationsTabProps } from './integrations-tab/integrations-tab-types'

import type { IntegrationsTabProps } from './integrations-tab/integrations-tab-types'

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function IntegrationsTab({
  integrations,
  isLoading = false,
  onConnect,
  onDisconnect,
  onSync,
  onOpenSettings,
  className,
}: IntegrationsTabProps) {
  const [connectingId, setConnectingId] = useState<string | null>(null)
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null)

  const handleConnect = useCallback(async (id: string) => {
    if (!onConnect) return
    setConnectingId(id)
    try {
      await onConnect(id)
    } finally {
      setConnectingId(null)
    }
  }, [onConnect])

  const handleDisconnect = useCallback(async (id: string) => {
    if (!onDisconnect) return
    setDisconnectingId(id)
    try {
      await onDisconnect(id)
    } finally {
      setDisconnectingId(null)
    }
  }, [onDisconnect])

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
      </div>
    )
  }

  const connectedIntegrations = integrations.filter(i => i.isConnected || i.status === 'connected' || i.status === 'expiring')
  const availableIntegrations = integrations.filter(i => {
    if (i.isConnected || i.status === 'connected' || i.status === 'expiring') return false
    if (i.status === 'planned') return false
    return true
  })
  const plannedIntegrations = integrations.filter(i => i.status === 'planned')

  return (
    <div className={cn('space-y-8', className)}>
      {/* Connected integrations */}
      {connectedIntegrations.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
            Connected Services
          </h3>
          <div className="space-y-3">
            {connectedIntegrations.map((integration) => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
                onConnect={() => handleConnect(integration.id)}
                onDisconnect={() => handleDisconnect(integration.id)}
                onSync={onSync ? () => onSync(integration.id) : undefined}
                onOpenSettings={onOpenSettings ? () => onOpenSettings(integration.id) : undefined}
                isConnecting={connectingId === integration.id}
                isDisconnecting={disconnectingId === integration.id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Available integrations */}
      {availableIntegrations.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
            Available Integrations
          </h3>
          <div className="space-y-3">
            {availableIntegrations.map((integration) => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
                onConnect={() => handleConnect(integration.id)}
                isConnecting={connectingId === integration.id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Planned integrations */}
      {plannedIntegrations.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
            Coming Soon
          </h3>
          <div className="space-y-3">
            {plannedIntegrations.map((integration) => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {integrations.length === 0 && (
        <div className="text-center py-12">
          <Zap className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
          <h3 className="font-medium text-slate-900 dark:text-white mb-2">
            No integrations available
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Integrations will appear here when they are configured for your organisation.
          </p>
        </div>
      )}
    </div>
  )
}

export default IntegrationsTab
